// src/services/firebase.ts
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
} from 'firebase/firestore';
import { functions, db, auth } from '../config/firebase';
import type {
  AgencyConfig,
  CreateAgencyRequest,
  GenerateTokenRequest,
  CloudFunctionResponse,
  AgencyStatsResponse,
  CreateAgencyWithTokenResponse,
} from '../types/agency';

/**
 * 現在認証されているユーザーのIDを取得
 */
function getCurrentUserId(): string {
  const currentUser = auth.currentUser;
  return currentUser?.email || 'anonymous';
}

/**
 * Firebase サービス（直接Firestore操作 + 必要に応じてCloud Functions）
 */
export class FirebaseService {
  /**
   * 代理店作成（直接Firestore書き込み）
   */
  static async createAgency(agencyData: CreateAgencyRequest): Promise<CloudFunctionResponse> {
    try {
      // 代理店IDの重複チェック
      const existingAgency = await getDoc(doc(db, 'agencies', agencyData.agencyId));
      if (existingAgency.exists()) {
        return {
          success: false,
          error: 'この代理店IDは既に使用されています',
        };
      }

      // 新しい代理店データを作成
      const newAgency: AgencyConfig = {
        agencyId: agencyData.agencyId,
        agencyName: agencyData.agencyName,
        isActive: true,
        branding: {
          lineUrl: agencyData.branding?.lineUrl || '',
          applicationUrl: agencyData.branding?.applicationUrl || '',
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: getCurrentUserId(),
        lastModifiedBy: getCurrentUserId(),
      };

      // Firestoreに直接書き込み
      await setDoc(doc(db, 'agencies', agencyData.agencyId), newAgency);

      return {
        success: true,
        data: newAgency,
      };
    } catch (error) {
      console.error('Failed to create agency:', error);
      return {
        success: false,
        error: '代理店の作成に失敗しました',
      };
    }
  }

  /**
   * 代理店作成とトークン生成を同時に実行（新機能）
   */
  static async createAgencyWithToken(agencyData: CreateAgencyRequest): Promise<CreateAgencyWithTokenResponse> {
    try {
      // 代理店IDの重複チェック
      const existingAgency = await getDoc(doc(db, 'agencies', agencyData.agencyId));
      if (existingAgency.exists()) {
        return {
          success: false,
          error: 'この代理店IDは既に使用されています',
        };
      }

      // 新しい代理店データを作成
      const newAgency: AgencyConfig = {
        agencyId: agencyData.agencyId,
        agencyName: agencyData.agencyName,
        isActive: true,
        branding: {
          lineUrl: agencyData.branding?.lineUrl || '',
          applicationUrl: agencyData.branding?.applicationUrl || '',
        },
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: getCurrentUserId(),
        lastModifiedBy: getCurrentUserId(),
      };

      // Firestoreに直接書き込み
      await setDoc(doc(db, 'agencies', agencyData.agencyId), newAgency);

      // トークン生成を実行（デフォルトで無期限）
      const tokenRequest: GenerateTokenRequest = {
        agencyId: agencyData.agencyId,
        expirationHours: agencyData.tokenExpirationHours || 0, // 0 = 無期限
      };

      const tokenResponse = await this.generateToken(tokenRequest);

      if (tokenResponse.success) {
        // 代理店情報を更新してトークン情報を記録
        const updateData = {
          lastTokenGenerated: Timestamp.now(),
          lastShortTokenId: tokenResponse.shortTokenId,
          updatedAt: Timestamp.now(),
          lastModifiedBy: getCurrentUserId(),
        };

        await updateDoc(doc(db, 'agencies', agencyData.agencyId), updateData);

        // 更新された代理店情報を含める
        const updatedAgency = {
          ...newAgency,
          ...updateData,
        };

        return {
          success: true,
          agency: updatedAgency,
          token: {
            customToken: tokenResponse.customToken!,
            shortTokenId: tokenResponse.shortTokenId!,
            accessUrl: tokenResponse.accessUrl!,
            expiresAt: tokenResponse.expiresAt,
          },
        };
      } else {
        // トークン生成に失敗した場合でも代理店は作成されている
        return {
          success: false,
          error: '代理店は作成されましたが、トークン生成に失敗しました。手動でトークンを生成してください。',
          agency: newAgency,
        };
      }
    } catch (error) {
      console.error('Failed to create agency with token:', error);
      return {
        success: false,
        error: '代理店とトークンの作成に失敗しました',
      };
    }
  }

  // トークン生成が必要な場合はCloud Functionsを使用したまま
  /**
   * アクセストークン生成（Cloud Functions使用）
   */
  static async generateToken(request: GenerateTokenRequest): Promise<CloudFunctionResponse> {
    try {
      const generateTokenFn = httpsCallable(functions, 'generateAgencyToken');
      const result = await generateTokenFn(request);
      return result.data as CloudFunctionResponse;
    } catch (error) {
      console.error('Failed to generate token:', error);
      throw new Error('トークンの生成に失敗しました');
    }
  }

  /**
   * 代理店一覧取得（直接Firestoreから取得）
   */
  static async listAgencies(options?: {
    limit?: number;
    startAfter?: string;
    filterActive?: boolean;
  }): Promise<CloudFunctionResponse> {
    try {
      let agenciesQuery = query(collection(db, 'agencies'), orderBy('createdAt', 'desc'));

      // アクティブフィルター
      if (options?.filterActive !== undefined) {
        agenciesQuery = query(agenciesQuery, where('isActive', '==', options.filterActive));
      }

      // 件数制限
      if (options?.limit) {
        agenciesQuery = query(agenciesQuery, limit(options.limit));
      }

      const snapshot = await getDocs(agenciesQuery);
      const agencies = snapshot.docs.map((doc) => ({
        agencyId: doc.id,
        ...doc.data(),
      })) as AgencyConfig[];

      return {
        success: true,
        agencies,
      };
    } catch (error) {
      console.error('Failed to list agencies:', error);
      return {
        success: false,
        error: '代理店一覧の取得に失敗しました',
      };
    }
  }

  /**
   * 代理店情報更新（直接Firestore更新）
   */
  static async updateAgency(agencyId: string, updates: Partial<AgencyConfig>): Promise<CloudFunctionResponse> {
    try {
      // 代理店の存在チェック
      const agencyDoc = await getDoc(doc(db, 'agencies', agencyId));
      if (!agencyDoc.exists()) {
        return {
          success: false,
          error: '指定された代理店が見つかりません',
        };
      }

      // 更新データに updatedAt を追加
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now(),
        lastModifiedBy: getCurrentUserId(),
      };

      // Firestoreを更新
      await updateDoc(doc(db, 'agencies', agencyId), updateData);

      return {
        success: true,
        data: updateData,
      };
    } catch (error) {
      console.error('Failed to update agency:', error);
      return {
        success: false,
        error: '代理店情報の更新に失敗しました',
      };
    }
  }

  /**
   * 代理店無効化（直接Firestore更新）
   */
  static async deactivateAgency(agencyId: string, reason?: string): Promise<CloudFunctionResponse> {
    try {
      // 代理店の存在チェック
      const agencyDoc = await getDoc(doc(db, 'agencies', agencyId));
      if (!agencyDoc.exists()) {
        return {
          success: false,
          error: '指定された代理店が見つかりません',
        };
      }

      // 無効化データ
      const updateData = {
        isActive: false,
        updatedAt: Timestamp.now(),
        lastModifiedBy: getCurrentUserId(),
        deactivatedReason: reason || '管理者による無効化',
        deactivatedAt: Timestamp.now(),
      };

      // Firestoreを更新
      await updateDoc(doc(db, 'agencies', agencyId), updateData);

      return {
        success: true,
        data: updateData,
      };
    } catch (error) {
      console.error('Failed to deactivate agency:', error);
      return {
        success: false,
        error: '代理店の無効化に失敗しました',
      };
    }
  }

  /**
   * 代理店詳細取得（Firestore直接アクセス）
   */
  static async getAgencyDetails(agencyId: string): Promise<AgencyConfig | null> {
    try {
      const agencyDoc = await getDoc(doc(db, 'agencies', agencyId));
      if (agencyDoc.exists()) {
        return { agencyId, ...agencyDoc.data() } as AgencyConfig;
      }
      return null;
    } catch (error) {
      console.error('Failed to get agency details:', error);
      throw new Error('代理店詳細の取得に失敗しました');
    }
  }

  /**
   * アクセスログ取得（最新100件） - 簡素化版
   */
  static async getRecentAccessLogs(_agencyId?: string) {
    try {
      // 簡素化された構造ではアクセスログの管理もシンプルに
      // 必要に応じて実装するか、または空の結果を返す
      return [];
    } catch (error) {
      console.error('Failed to get access logs:', error);
      throw new Error('アクセスログの取得に失敗しました');
    }
  }

  /**
   * 代理店の統計情報取得
   */
  static async getAgencyStats(
    agencyId: string,
    dateRange: 'week' | 'month' | 'year' = 'month'
  ): Promise<AgencyStatsResponse> {
    try {
      // 代理店情報を取得
      const agencyDoc = await getDoc(doc(db, 'agencies', agencyId));
      if (!agencyDoc.exists()) {
        throw new Error('代理店が見つかりません');
      }

      const agencyData = agencyDoc.data() as AgencyConfig;

      // 期間の計算
      const now = new Date();
      const startDate = new Date();

      switch (dateRange) {
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      // 基本統計（簡素化版 - 必要に応じてアクセスログコレクションから取得）
      const basic = {
        totalUsers: 0,
        totalSimulations: 0,
        totalPDFExports: 0,
        activeUsers: 0,
        lastAccessed: agencyData.updatedAt || agencyData.createdAt,
      };

      // アクセスログがある場合は取得（オプション）
      try {
        const logsQuery = query(
          collection(db, 'accessLogs'),
          where('agencyId', '==', agencyId),
          where('timestamp', '>=', Timestamp.fromDate(startDate)),
          orderBy('timestamp', 'desc'),
          limit(1000)
        );

        const logsSnapshot = await getDocs(logsQuery);
        const logs = logsSnapshot.docs.map((doc) => doc.data());

        // ログからアクション別カウントを計算
        const actionCounts: { [key: string]: number } = {};
        const dailyStats: { [key: string]: any } = {};
        const uniqueUsers = new Set();

        logs.forEach((log) => {
          const action = log.action || 'unknown';
          actionCounts[action] = (actionCounts[action] || 0) + 1;

          if (log.userAgent || log.sessionId) {
            uniqueUsers.add(log.sessionId || log.userAgent);
          }

          // 日別統計
          const dateKey = log.timestamp.toDate().toDateString();
          if (!dailyStats[dateKey]) {
            dailyStats[dateKey] = { simulations: 0, pdfExports: 0, logins: 0 };
          }

          if (action === 'simulation') dailyStats[dateKey].simulations++;
          if (action === 'pdf_export') dailyStats[dateKey].pdfExports++;
          if (action === 'login') dailyStats[dateKey].logins++;
        });

        basic.totalSimulations = actionCounts.simulation || 0;
        basic.totalPDFExports = actionCounts.pdf_export || 0;
        basic.activeUsers = uniqueUsers.size;
        basic.totalUsers = uniqueUsers.size; // 簡素化

        return {
          basic,
          period: {
            dateRange,
            startDate: startDate.toISOString(),
            endDate: now.toISOString(),
          },
          actionCounts,
          dailyStats,
          totalLogs: logs.length,
        };
      } catch (logError) {
        // ログコレクションが存在しない場合やアクセス権限がない場合
        console.warn('Access logs not available:', logError);

        return {
          basic,
          period: {
            dateRange,
            startDate: startDate.toISOString(),
            endDate: now.toISOString(),
          },
          actionCounts: {},
          dailyStats: {},
          totalLogs: 0,
        };
      }
    } catch (error) {
      console.error('Failed to get agency stats:', error);
      throw new Error('代理店統計の取得に失敗しました');
    }
  }

  /**
   * システム全体の統計取得（簡素化版）
   */
  static async getSystemStats() {
    try {
      const agenciesSnapshot = await getDocs(collection(db, 'agencies'));

      let totalAgencies = 0;
      let activeAgencies = 0;

      agenciesSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        totalAgencies++;
        if (data.isActive) activeAgencies++;
      });

      return {
        totalAgencies,
        activeAgencies,
        inactiveAgencies: totalAgencies - activeAgencies,
      };
    } catch (error) {
      console.error('Failed to get system stats:', error);
      throw new Error('システム統計の取得に失敗しました');
    }
  }

  /**
   * 短縮URL情報の取得 (tokenMappingsコレクションから)
   */
  static async getShortUrlInfo(shortTokenId: string): Promise<{
    accessUrl?: string;
    agencyId?: string;
    agencyName?: string;
    expiresAt?: string;
    isValid?: boolean;
  } | null> {
    try {
      // 実際の実装では、Cloud Functionsまたは直接FirestoreのtokenMappingsコレクションからデータを取得
      // 現在は簡素化された形で、基本的な情報を返す

      // Cloud FunctionのcheckShortIdStatus関数を呼び出す場合
      const checkStatusFn = httpsCallable(functions, 'checkShortIdStatus');
      const result = await checkStatusFn({ shortTokenId });
      const response = result.data as CloudFunctionResponse;

      if (response.success) {
        return {
          accessUrl: `https://ikiiki-smile-agency.jp/?id=${shortTokenId}`,
          agencyId: response.agencyInfo?.agencyId,
          agencyName: response.agencyInfo?.agencyName,
          expiresAt: response.expiresAt,
          isValid: true,
        };
      }

      return null;
    } catch (error) {
      console.error('Failed to get short URL info:', error);
      return null;
    }
  }

  /**
   * 編集用トークンの検証
   */
  static async validateEditToken(shortTokenId: string): Promise<{
    success: boolean;
    agencyId?: string;
    agencyData?: AgencyConfig;
    subscriptionStatus?: string;
    isSubscriptionActive?: boolean;
    error?: string;
  }> {
    try {
      const validateToken = httpsCallable<
        { shortTokenId: string },
        {
          success: boolean;
          agencyId?: string;
          agencyData?: AgencyConfig;
          subscriptionStatus?: string;
          isSubscriptionActive?: boolean;
          error?: string;
        }
      >(functions, 'validateEditToken');

      const result = await validateToken({ shortTokenId });
      return result.data;
    } catch (error) {
      console.error('Failed to validate edit token:', error);
      return {
        success: false,
        error: 'トークンの検証に失敗しました',
      };
    }
  }

  /**
   * サブスクリプションステータスを取得
   */
  static async getSubscriptionStatus(agencyId: string): Promise<{
    success: boolean;
    hasSubscription?: boolean;
    status?: string;
    cancelAtPeriodEnd?: boolean;
    cancelAt?: string | null;
    nextBillingDate?: string | null;
    error?: string;
  }> {
    try {
      const getStatus = httpsCallable<
        { agencyId: string },
        {
          success: boolean;
          hasSubscription?: boolean;
          status?: string;
          cancelAtPeriodEnd?: boolean;
          cancelAt?: string | null;
          nextBillingDate?: string | null;
          error?: string;
        }
      >(functions, 'getSubscriptionStatus');

      const result = await getStatus({ agencyId });
      return result.data;
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return {
        success: false,
        error: 'サブスクリプション情報の取得に失敗しました',
      };
    }
  }

  /**
   * Stripe Customer Portalセッションを作成
   */
  static async createCustomerPortalSession(
    agencyId: string,
    shortTokenId?: string
  ): Promise<{
    success: boolean;
    url?: string;
    error?: string;
  }> {
    try {
      const createSession = httpsCallable<
        { agencyId: string; shortTokenId?: string },
        { success: boolean; url?: string; error?: string }
      >(functions, 'createCustomerPortalSession');

      const result = await createSession({ agencyId, shortTokenId });
      return result.data;
    } catch (error) {
      console.error('Failed to create customer portal session:', error);
      return {
        success: false,
        error: 'ポータルセッションの作成に失敗しました',
      };
    }
  }
}
